/* REACT */
import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

/* ROUTER */
import { useNavigate } from 'react-router-dom';

/* PLUGINS */
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

/* SERVICES */
import { authService } from '../services/authService';

/* STORE */
import { useAuthStore } from '@/store/authStore';

/* SCHEMAS */
import { otpSchema, type OtpFormData } from '../schemas';

/* COMPONENTS */
import { AuthLayout } from './AuthLayout';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const ONE = 1;
const ZERO = 0;
const COUNTDOWN_INTERVAL_MS = 1000;
const AUTH_CARD_CLASS = [
    'w-full max-w-[480px] rounded-lg border border-[#e5e5e5] bg-white',
    'flex flex-col items-center px-8 py-8 md:px-10 gap-8 text-center',
].join(' ');
const PRIMARY_BUTTON_CLASS = [
    'self-stretch rounded-lg bg-brand flex items-center justify-center px-4 py-3',
    'cursor-pointer text-white border-none text-sm leading-5 tracking-[0.25px]',
    'font-medium hover:bg-brand-hover disabled:bg-[#e5e5e5]',
    'disabled:text-[rgba(46,27,20,0.4)]',
].join(' ');
const LOADING_CARD_CLASS = [
    'w-full max-w-[480px] rounded-lg border border-[#e5e5e5] bg-white',
    'px-8 py-8 text-center',
].join(' ');
const OTP_INPUT_BASE_CLASS = [
    'size-10 md:size-14 text-center text-2xl font-medium',
    'rounded-lg border bg-white outline-none focus:border-brand',
    'text-[#281e1b]',
].join(' ');
const OTP_FOOTER_CLASS = [
    'self-stretch flex flex-col items-center gap-4',
    'text-sm leading-5 text-[#525252]',
].join(' ');

interface PendingOtpAuth {
    user_id: number;
    email_address: string;
    role_type_id: number;
    otp_challenge_token: string;
}

/**
 * DOCU: Renders PO-style OTP verification form. <br>
 * Triggered: User is redirected to /otp after OTP-required login. <br>
 * Last Updated Date: May 19, 2026 <br>
 * @returns {JSX.Element} OTP verification screen.
 * @author Sam
 */
export const OtpForm = () => {
    const navigate = useNavigate();
    const query_client = useQueryClient();
    const setUser = useAuthStore((state) => state.setUser);
    const [pending_auth, setPendingAuth] = useState<PendingOtpAuth | null>(null);
    const [otp_values, setOtpValues] = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [is_loading, setIsLoading] = useState(false);
    const [is_resending, setIsResending] = useState(false);
    const [resend_countdown, setResendCountdown] = useState(RESEND_SECONDS);
    const [trust_device, setTrustDevice] = useState(false);
    const input_refs = useRef<(HTMLInputElement | null)[]>([]);
    const {
        formState: { errors },
        handleSubmit,
        setError,
        setValue,
    } = useForm<OtpFormData>({
        resolver: zodResolver(otpSchema),
    });

    useEffect(() => {
        const stored_auth = sessionStorage.getItem('otp_pending_auth');

        if (!stored_auth) {
            navigate('/login', { replace: true });
            return;
        }

        setPendingAuth(JSON.parse(stored_auth) as PendingOtpAuth);
    }, [navigate]);

    useEffect(() => {
        if (resend_countdown <= ZERO) {
            return undefined;
        }

        const timeout_id = window.setTimeout(() => {
            setResendCountdown((current_countdown) => current_countdown - ONE);
        }, COUNTDOWN_INTERVAL_MS);

        return () => window.clearTimeout(timeout_id);
    }, [resend_countdown]);

    /**
     * DOCU: Update OTP digit and move focus forward. <br>
     * Triggered: OTP input change. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {number} index - OTP digit index.
     * @param {string} value - Input value.
     * @returns {void}
     * @author Sam
     */
    const handleOtpChange = (index: number, value: string): void => {
        if (value && !/^\d$/.test(value)) {
            return;
        }

        const next_values = [...otp_values];
        next_values[index] = value;
        setOtpValues(next_values);
        setValue('otp', next_values.join(''));

        if (value && index < OTP_LENGTH - ONE) {
            input_refs.current[index + ONE]?.focus();
        }
    };

    /**
     * DOCU: Move OTP focus backward on backspace. <br>
     * Triggered: OTP input keydown. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {number} index - OTP digit index.
     * @param {KeyboardEvent<HTMLInputElement>} event - Keyboard event.
     * @returns {void}
     * @author Sam
     */
    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Backspace' && !otp_values[index] && index > ZERO) {
            input_refs.current[index - ONE]?.focus();
        }
    };

    /**
     * DOCU: Paste numeric OTP digits across OTP inputs. <br>
     * Triggered: OTP input paste. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {ClipboardEvent<HTMLInputElement>} event - Clipboard event.
     * @returns {void}
     * @author Sam
     */
    const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
        event.preventDefault();
        const pasted_digits = event.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(ZERO, OTP_LENGTH)
            .split('');
        const next_values = [...otp_values];

        for (const [index, digit] of pasted_digits.entries()) {
            next_values[index] = digit;
        }

        setOtpValues(next_values);
        setValue('otp', next_values.join(''));
    };

    /**
     * DOCU: Verify OTP and complete login. <br>
     * Triggered: OTP form submit. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {OtpFormData} data - OTP form payload.
     * @returns {Promise<void>}
     * @author Sam
     */
    const onSubmit = async (data: OtpFormData): Promise<void> => {
        if (!pending_auth) {
            navigate('/login', { replace: true });
            return;
        }

        setIsLoading(true);

        try {
            const response = await authService.verifyOtp({
                otp_challenge_token: pending_auth.otp_challenge_token,
                otp_code: data.otp,
                trust_device,
            });

            const { user, access_token, refresh_token } = response.result;

            if (!response.result.is_valid || !user || !access_token) {
                setError('otp', { type: 'manual', message: 'Invalid verification code.' });
                return;
            }

            setUser(user, access_token, refresh_token);
            sessionStorage.removeItem('otp_pending_auth');
            query_client.invalidateQueries();
            navigate('/dashboard');
        }
        catch {
            setError('otp', {
                type: 'manual',
                message: 'Failed to verify code. Please try again.',
            });
        }
        finally {
            setIsLoading(false);
        }
    };

    /**
     * DOCU: Resend OTP and reset OTP fields/countdown. <br>
     * Triggered: Resend OTP button click. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @returns {Promise<void>}
     * @author Sam
     */
    const handleResendOtp = async (): Promise<void> => {
        if (!pending_auth || resend_countdown > ZERO) {
            return;
        }

        setIsResending(true);

        try {
            await authService.resendOtp({
                otp_challenge_token: pending_auth.otp_challenge_token,
            });
            setOtpValues(Array(OTP_LENGTH).fill(''));
            setValue('otp', '');
            setResendCountdown(RESEND_SECONDS);
            input_refs.current[ZERO]?.focus();
        }
        finally {
            setIsResending(false);
        }
    };

    if (!pending_auth) {
        return (
            <AuthLayout title="">
                <div className={LOADING_CARD_CLASS}>
                    Loading...
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title="">
            <div className={AUTH_CARD_CLASS}>
                <div className="self-stretch flex flex-col items-center gap-3">
                    <h1 className="self-stretch text-[28px] leading-9 font-medium text-[#363636]">
                        Enter Verification Code
                    </h1>
                    <p className="self-stretch text-base leading-6 tracking-[0.5px] text-[#525252]">
                        We sent a 6-digit code to {pending_auth.email_address}
                    </p>
                </div>

                <form
                    className="self-stretch flex flex-col items-center gap-6"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <div className="flex items-center justify-center gap-2 md:gap-3">
                        {otp_values.map((value, index) => (
                            <input
                                key={`otp-input-${index}`}
                                ref={(element) => {
                                    input_refs.current[index] = element;
                                }}
                                type="text"
                                inputMode="numeric"
                                maxLength={ONE}
                                value={value}
                                onChange={(event) => handleOtpChange(index, event.target.value)}
                                onKeyDown={(event) => handleKeyDown(index, event)}
                                onPaste={handlePaste}
                                className={[
                                    OTP_INPUT_BASE_CLASS,
                                    errors.otp
                                        ? 'border-[#dc2626] border-[1.5px]'
                                        : 'border-[#e5e5e5]',
                                ].join(' ')}
                            />
                        ))}
                    </div>

                    {errors.otp && (
                        <div className="text-xs leading-4 tracking-[0.25px] text-[#dc2626]">
                            {errors.otp.message}
                        </div>
                    )}

                    <label
                        className={[
                            'self-start flex items-center gap-2 cursor-pointer text-sm',
                            'leading-5 text-[#525252]',
                        ].join(' ')}
                    >
                        <input
                            type="checkbox"
                            checked={trust_device}
                            onChange={(event) => setTrustDevice(event.target.checked)}
                            disabled={is_loading}
                            className="size-4 accent-brand"
                        />
                        Don't ask again on this device
                    </label>

                    <button
                        type="submit"
                        disabled={is_loading}
                        className={PRIMARY_BUTTON_CLASS}
                    >
                        {is_loading ? 'Verifying...' : 'Verify Code'}
                    </button>
                </form>

                <div className={OTP_FOOTER_CLASS}>
                    <div>
                        Didn't receive the code?{' '}
                        <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={is_resending || resend_countdown > ZERO}
                        className={[
                            'text-brand font-medium hover:underline bg-transparent border-none',
                            'cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                        ].join(' ')}
                    >
                            {is_resending
                                ? 'Sending...'
                                : resend_countdown > ZERO
                                    ? `Resend in ${resend_countdown}s`
                                    : 'Resend'}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            sessionStorage.removeItem('otp_pending_auth');
                            navigate('/login');
                        }}
                        className={[
                            'text-[#737373] hover:text-[#171717] hover:underline',
                            'bg-transparent border-none cursor-pointer',
                        ].join(' ')}
                    >
                        Back to Sign In
                    </button>
                </div>
            </div>
        </AuthLayout>
    );
};
